/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useRef, ChangeEvent } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Flex,
  Text,
  Input,
  Button,
  useToast,
  InputGroup,
  InputLeftElement,
  Box,
  HStack,
  Tag,
} from '@chakra-ui/react';
import { CashfreeInstance, PaymentDetails } from '@cashfreepayments/cashfree-js';
import useCreatePaymentOrder from '../queries/useCreatePaymentOrder';
import useAddLiveCredits from '../hooks/useAddLiveCredits';

interface AddCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPaymentSuccess?: (details: PaymentDetails) => void;
  availableCredits: number;
}

const AddCreditsModal: React.FC<AddCreditsModalProps> = ({ isOpen, onClose, onPaymentSuccess, availableCredits }) => {
  const { handleLiveCredits } = useAddLiveCredits({
    onSuccess: (paymentDetails: PaymentDetails) => {
      //   handlePaymentSuccess(paymentDetails);
    },
    onError: error => console.error('Payment error:', error),
  });

  const [credits, setCredits] = useState<string>('1000');
  const [cashfree, setCashfree] = useState<CashfreeInstance | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const creditOptions = [
    { value: '1000', label: '₹ 1000' },
    { value: '3000', label: '₹ 3000', isPopular: true },
    { value: '5000', label: '₹ 5000' },
  ];

  const { mutate: createPaymentOrder, isLoading: isCreatingOrder } = useCreatePaymentOrder({
    onSuccess: data => {
      console.log('data', data);
      onClose();
      handleLiveCredits(data.paymentId);
    },
    onError: error => {
      console.error('Error creating payment order:', error);
      toast({
        title: 'Order Creation Failed',
        description: error.message || 'Failed to create payment order',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Handle input change
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Allow only digits
    setCredits(value);
  };

  // Handle credit option selection
  const handleCreditOptionClick = (value: string) => {
    setCredits(value);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle proceed button click
  const handleProceed = () => {
    if (!credits || Number(credits) <= 0) {
      toast({
        title: 'Invalid Amount',
        description: 'Please enter a valid amount',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    createPaymentOrder({ amount: Number(credits), currency: 'INR' });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="rgba(130, 97, 97, 0.4)" />
      <ModalContent borderRadius="xl" py={4}>
        <ModalCloseButton />
        <ModalBody p={6}>
          {/* Background with light blue gradient */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            borderRadius="xl"
            bg="blue.50"
            opacity={0.8}
            zIndex={-1}
          />

          <Flex direction="column" py={2}>
            {/* Header */}
            <Flex justify="space-between" align="center" mb={6}>
              <Text fontSize="3xl" fontWeight="bold" color="blue.700">
                Credits
              </Text>
              <Box bg="green.50" px={4} py={1} borderRadius="full" border="1px solid" borderColor="green.200">
                <Text color="gray.800" fontSize="sm">
                  Available Credits: <strong>{availableCredits}</strong>
                </Text>
              </Box>
            </Flex>

            {/* Add credits section */}
            <Text fontSize="xl" fontWeight="semibold" color="blue.700" mb={1}>
              Add credits
            </Text>
            <Text color="blue.700" mb={1}>
              Enter the desired credits to add to the platform.
            </Text>
            <Text color="blue.700" mb={4}>
              Pricing (1 credit = 1 rupee)
            </Text>

            {/* Credits input */}
            <InputGroup size="lg" mb={4}>
              <InputLeftElement pointerEvents="none" color="gray.500">
                ₹
              </InputLeftElement>
              <Input
                ref={inputRef}
                placeholder="Eg. 1000"
                value={credits}
                onChange={handleInputChange}
                bg="white"
                borderRadius="md"
                borderColor="gray.200"
                _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
                height="56px"
                fontSize="lg"
              />
            </InputGroup>

            {/* Credit options */}
            <HStack spacing={3} mb={6}>
              {creditOptions.map(option => (
                <Box key={option.value} position="relative">
                  <Button
                    variant="outline"
                    size="md"
                    onClick={() => handleCreditOptionClick(option.value)}
                    borderRadius="full"
                    borderColor="gray.300"
                    color="gray.800"
                    bg="white"
                    _hover={{ bg: 'gray.50' }}
                    height="36px"
                  >
                    {option.label}
                  </Button>
                  {option.isPopular && (
                    <Tag
                      position="absolute"
                      top="-8px"
                      left="50%"
                      transform="translateX(-50%)"
                      size="sm"
                      colorScheme="orange"
                      borderRadius="full"
                      fontWeight="medium"
                    >
                      Popular
                    </Tag>
                  )}
                </Box>
              ))}
            </HStack>

            {/* Proceed button */}
            <Button
              colorScheme="blue"
              size="lg"
              onClick={handleProceed}
              isLoading={isCreatingOrder}
              loadingText="Processing"
              borderRadius="md"
              height="56px"
              fontSize="lg"
              alignSelf="flex-end"
              px={8}
            >
              Proceed
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AddCreditsModal;
